import { useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { handleWalletUpdater, handleOrderUpdater } from '@store/notification';
import { fetchWallet } from '../../redux/wallet';

/**
 * Standardized Wallet Cache Manager
 * Provides consistent cache invalidation across the entire application
 *
 * NOTE: This file is being refactored as part of comprehensive WebSocket/Cache fix
 * See: WEBSOCKET_CACHE_REFACTOR_PLAN.md for details
 */

// Centralized cache keys for wallet-related data
export const WALLET_CACHE_KEYS = {
  // Main wallet list
  LIST: ['fetchWallet'],
  
  // Wallet summary data
  SUMMARY: ['fetchWalletSummary'],
  
  // User top wallets
  TOP_WALLETS: ['fetchUserTopWallets'],
  
  // Transaction-related caches that affect wallet balances
  DEPOSIT_TRANSACTIONS: (page, query) => ['fetchDepositTransanction', page, query],
  WITHDRAW_TRANSACTIONS: (page, query) => ['fetchWithDrawTransactions', page, query],
  INTERNAL_TRANSFERS: (page, query) => ['fetchInternalTransferHistory', page, query],
  
  // Order-related caches that affect wallet balances
  ORDERS: (page, query) => ['fetchOrder', page, query],
  ORDER_HISTORY: (page, query) => ['fetchOrderHistory', page, query],
  
  // All wallet-related cache keys pattern
  ALL_WALLET_RELATED: ['fetchWallet', 'fetchWalletSummary', 'fetchUserTopWallets']
};

// Cache invalidation reasons for debugging and analytics
export const INVALIDATION_REASONS = {
  WEBSOCKET_UPDATE: 'websocket_wallet_update',
  TRADE_EXECUTED: 'trade_executed',
  DEPOSIT_COMPLETED: 'deposit_completed',
  WITHDRAWAL_PROCESSED: 'withdrawal_processed',
  TRANSACTION_CANCELLED: 'transaction_cancelled',
  ORDER_CANCELLED: 'order_cancelled',
  GIFT_CODE_REDEEMED: 'gift_code_redeemed',
  INTERNAL_TRANSFER: 'internal_transfer',
  MANUAL_REFRESH: 'manual_refresh',
  ERROR_RECOVERY: 'error_recovery',
  BACKGROUND_SYNC: 'background_sync',
  USER_ACTION: 'user_action',
  WINDOW_FOCUS: 'window_focus',
  TRADING_ACTIVITY: 'trading_activity'
};

/**
 * Main wallet cache manager hook
 */
export const useWalletCacheManager = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const lastWebSocketInvalidation = useRef(0);

  /**
   * Standardized wallet cache invalidation
   * @param {string} reason - Reason for invalidation (for debugging)
   * @param {Object} options - Additional options
   */
  const invalidateWalletCache = useCallback((reason = INVALIDATION_REASONS.MANUAL_REFRESH, options = {}) => {
    const {
      includeTransactions = false,
      includeOrders = false,
      silent = false,
      forceRefetch = false
    } = options;

    if (!silent) {
      console.log(`🔄 Invalidating wallet cache: ${reason}`, {
        includeTransactions,
        includeOrders,
        forceRefetch,
        timestamp: new Date().toISOString()
      });
    }

    try {
      // Always invalidate core wallet data
      queryClient.invalidateQueries(WALLET_CACHE_KEYS.LIST, {
        refetchType: forceRefetch ? 'active' : 'inactive'
      });
      
      queryClient.invalidateQueries(WALLET_CACHE_KEYS.SUMMARY, {
        refetchType: forceRefetch ? 'active' : 'inactive'
      });
      
      queryClient.invalidateQueries(WALLET_CACHE_KEYS.TOP_WALLETS, {
        refetchType: forceRefetch ? 'active' : 'inactive'
      });

      // Conditionally invalidate related data
      if (includeTransactions) {
        // Invalidate all transaction-related queries
        queryClient.invalidateQueries(['fetchDepositTransanction']);
        queryClient.invalidateQueries(['fetchWithDrawTransactions']);
        queryClient.invalidateQueries(['fetchInternalTransferHistory']);
      }

      if (includeOrders) {
        // Invalidate all order-related queries with proper cache key matching
        queryClient.invalidateQueries({ queryKey: ['fetchOrder'] });
        queryClient.invalidateQueries({ queryKey: ['fetchOrderHistory'] });

        // Also clear the in-memory order cache to ensure fresh API calls
        if (typeof window !== 'undefined' && window.orderCache) {
          window.orderCache.clear();
        }
      }

      // Update Redux state to trigger UI updates
      dispatch(handleWalletUpdater());

      // Track invalidation for analytics (in development, but skip frequent WebSocket updates)
      if (process.env.NODE_ENV === 'development' && reason !== INVALIDATION_REASONS.WEBSOCKET_UPDATE) {
        trackCacheInvalidation(reason, options);
      }

    } catch (error) {
      console.error('❌ Error during wallet cache invalidation:', error);
      
      // Fallback: Force clear all wallet-related cache
      WALLET_CACHE_KEYS.ALL_WALLET_RELATED.forEach(key => {
        queryClient.removeQueries([key]);
      });
    }
  }, [queryClient, dispatch]);

  /**
   * Refresh wallet data using Redux (for backward compatibility)
   */
  const refreshWalletRedux = useCallback(() => {
    console.log('🔄 Refreshing wallet via Redux');
    dispatch(fetchWallet());
  }, [dispatch]);

  /**
   * Combined refresh - both React Query and Redux
   */
  const refreshWalletComplete = useCallback((reason = INVALIDATION_REASONS.MANUAL_REFRESH) => {
    console.log(`🔄 Complete wallet refresh: ${reason}`);
    
    // Invalidate React Query cache
    invalidateWalletCache(reason, { forceRefetch: true });
    
    // Also refresh Redux for components that still use it
    refreshWalletRedux();
  }, [invalidateWalletCache, refreshWalletRedux]);

  /**
   * Clear order cache completely to ensure fresh API calls
   */
  const clearOrderCache = useCallback(() => {
    try {
      // Clear in-memory order cache
      if (typeof window !== 'undefined' && window.orderCache) {
        window.orderCache.clear();
        console.log('🗑️ Cleared in-memory order cache');
      }

      // Invalidate ALL React Query order cache entries (regardless of parameters)
      queryClient.invalidateQueries({
        queryKey: ['fetchOrder'],
        refetchType: 'active'
      });

      // Also remove all order-related queries to force fresh fetch
      queryClient.removeQueries(['fetchOrder']);

      // Trigger Redux order updater to refresh all order components
      dispatch(handleOrderUpdater());

      console.log('🔄 Force invalidated and removed all order cache entries');
    } catch (error) {
      console.error('❌ Error clearing order cache:', error);
    }
  }, [queryClient, dispatch]);

  /**
   * Smart invalidation based on operation type
   */
  const invalidateBasedOnOperation = useCallback((operationType, operationData = {}) => {
    switch (operationType) {
      case 'TRADE':
        console.log('🔄 TRADE operation detected - clearing order cache and invalidating wallet cache');
        // Clear order cache first to ensure fresh data
        clearOrderCache();
        invalidateWalletCache(INVALIDATION_REASONS.TRADE_EXECUTED, {
          includeOrders: true,
          forceRefetch: true
        });
        break;

      case 'DEPOSIT':
        invalidateWalletCache(INVALIDATION_REASONS.DEPOSIT_COMPLETED, {
          includeTransactions: true,
          forceRefetch: true
        });
        break;

      case 'WITHDRAWAL':
        invalidateWalletCache(INVALIDATION_REASONS.WITHDRAWAL_PROCESSED, {
          includeTransactions: true,
          forceRefetch: true
        });
        break;

      case 'TRANSACTION_CANCEL':
        invalidateWalletCache(INVALIDATION_REASONS.TRANSACTION_CANCELLED, {
          includeTransactions: true,
          includeOrders: true,
          forceRefetch: true
        });
        break;

      case 'ORDER_CANCEL':
        console.log('🔄 ORDER_CANCEL operation detected - clearing order cache and invalidating wallet cache');
        // Clear order cache first to ensure fresh data
        clearOrderCache();
        invalidateWalletCache(INVALIDATION_REASONS.ORDER_CANCELLED, {
          includeOrders: true,
          forceRefetch: true
        });
        break;

      case 'GIFT_CODE':
        invalidateWalletCache(INVALIDATION_REASONS.GIFT_CODE_REDEEMED, {
          forceRefetch: true
        });
        break;

      case 'INTERNAL_TRANSFER':
        invalidateWalletCache(INVALIDATION_REASONS.INTERNAL_TRANSFER, {
          includeTransactions: true,
          forceRefetch: true
        });
        break;

      case 'WEBSOCKET':
        // Throttle WebSocket invalidations to prevent excessive updates
        const now = Date.now();
        const timeSinceLastInvalidation = now - lastWebSocketInvalidation.current;

        // Only invalidate if it's been more than 1 second since last invalidation
        if (timeSinceLastInvalidation > 1000) {
          lastWebSocketInvalidation.current = now;
          invalidateWalletCache(INVALIDATION_REASONS.WEBSOCKET_UPDATE, {
            silent: true, // Don't log WebSocket updates as they're frequent
            forceRefetch: false // Let React Query decide when to refetch
          });
        }
        break;

      case 'WEBSOCKET_ORDER':
        console.log('🔄 WEBSOCKET_ORDER operation detected - clearing order cache');
        // Clear order cache for WebSocket order updates
        clearOrderCache();
        invalidateWalletCache(INVALIDATION_REASONS.WEBSOCKET_UPDATE, {
          includeOrders: true,
          forceRefetch: true
        });
        break;

      default:
        invalidateWalletCache(INVALIDATION_REASONS.USER_ACTION);
    }
  }, [invalidateWalletCache]);

  /**
   * Get current cache status for debugging
   */
  const getCacheStatus = useCallback(() => {
    const walletQuery = queryClient.getQueryState(WALLET_CACHE_KEYS.LIST);
    const summaryQuery = queryClient.getQueryState(WALLET_CACHE_KEYS.SUMMARY);
    
    return {
      wallet: {
        status: walletQuery?.status || 'idle',
        dataUpdatedAt: walletQuery?.dataUpdatedAt || null,
        isStale: walletQuery?.isStale || false,
        isFetching: walletQuery?.isFetching || false
      },
      summary: {
        status: summaryQuery?.status || 'idle',
        dataUpdatedAt: summaryQuery?.dataUpdatedAt || null,
        isStale: summaryQuery?.isStale || false,
        isFetching: summaryQuery?.isFetching || false
      },
      cacheSize: queryClient.getQueryCache().getAll().length
    };
  }, [queryClient]);

  /**
   * Error recovery invalidation
   */
  const invalidateOnError = useCallback((error, context = 'unknown') => {
    // Skip cache invalidation if this is a token expiration error
    if (error._handledByTokenExpiration || error?.status === 401) {
      console.log(`✅ Skipping cache invalidation for ${context} - token expiration in progress`);
      return;
    }
    console.warn(`🚨 Error-based cache invalidation triggered: ${context}`, error);
    invalidateWalletCache(INVALIDATION_REASONS.ERROR_RECOVERY, {
      forceRefetch: true,
      includeTransactions: true,
      includeOrders: true
    });
  }, [invalidateWalletCache]);

  /**
   * Background sync for active trading users
   */
  const backgroundSync = useCallback(() => {
    invalidateWalletCache(INVALIDATION_REASONS.BACKGROUND_SYNC, {
      silent: true,
      forceRefetch: false
    });
  }, [invalidateWalletCache]);

  /**
   * Window focus refresh for wallet pages
   */
  const refreshOnWindowFocus = useCallback(() => {
    invalidateWalletCache(INVALIDATION_REASONS.WINDOW_FOCUS, {
      forceRefetch: false // Let React Query decide based on stale time
    });
  }, [invalidateWalletCache]);

  return {
    // Main invalidation methods
    invalidateWalletCache,
    refreshWalletRedux,
    refreshWalletComplete,
    invalidateBasedOnOperation,

    // Enhanced methods
    invalidateOnError,
    backgroundSync,
    refreshOnWindowFocus,
    clearOrderCache,

    // Utility methods
    getCacheStatus,

    // Constants for external use
    WALLET_CACHE_KEYS,
    INVALIDATION_REASONS
  };
};

/**
 * Track cache invalidation for analytics and debugging
 */
const trackCacheInvalidation = (reason, options) => {
  const event = {
    type: 'wallet_cache_invalidation',
    reason,
    options,
    timestamp: Date.now(),
    url: window.location.pathname
  };
  
  console.log('📊 Cache invalidation tracked:', event);
  
  // In production, send to analytics service
  if (process.env.NODE_ENV === 'production' && window.analytics) {
    window.analytics.track('wallet_cache_invalidation', event);
  }
};

/**
 * Legacy compatibility wrapper
 * Provides the old refetchWallet function for existing code
 */
export const useLegacyWalletRefetch = () => {
  const { invalidateWalletCache } = useWalletCacheManager();
  
  // Legacy function that existing code expects
  const refetchWallet = useCallback(() => {
    invalidateWalletCache(INVALIDATION_REASONS.MANUAL_REFRESH, { forceRefetch: true });
  }, [invalidateWalletCache]);
  
  return { refetchWallet };
};

export default useWalletCacheManager;
